import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import InputText from './InputText';
import Dropdown from './Dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { MultiSelect } from 'primereact/multiselect';

const DynamicDataTable = ({ columns, filterFields, data, totalRecords, loading, onLoadData, header }) => {
    const toast = useRef(null);

    const [visibleColumns, setVisibleColumns] = useState(columns);
    const [visibleSidebar, setVisibleSidebar] = useState(false);
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: null,
        sortOrder: null,
    });

    useEffect(() => {
        onLoadData(lazyParams, appliedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams, appliedFilters]);

    const onColumnToggle = (event) => {
        let selectedColumns = event.value;
        let orderedSelectedColumns = columns.filter((col) =>
            selectedColumns.some((sCol) => sCol.field === col.field)
        );

        setVisibleColumns(orderedSelectedColumns);
    };

    const onPage = (event) => {
        setLazyParams({
            ...lazyParams,
            first: event.first,
            rows: event.rows,
            page: event.page,
        });
    };

    const onSort = (event) => {
        setLazyParams({
            ...lazyParams,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            first: 0,
        });
    };

    const onFilterApply = () => {
        setAppliedFilters({ ...filters });
        setLazyParams({ ...lazyParams, first: 0, page: 0 });
        setVisibleSidebar(false);
    };

    const onFilterClear = () => {
        setFilters({});
        setAppliedFilters({});
        setLazyParams({ ...lazyParams, first: 0, page: 0 });
        setVisibleSidebar(false);
    };

    const rowClassName = (rowData) => {
        return {
            'row-active': rowData.status && rowData.status.id === 3,
            'row-inactive': !rowData.status || rowData.status.id !== 3,
        };
    };

    const renderFilterInputs = () => {
        return filterFields.map((field) => {
            switch (field.type) {
                case 'text':
                case 'number':
                    return (
                        <div key={field.field} className="p-field">
                            <InputText
                                id={field.field}
                                name={field.field}
                                value={filters[field.field] || ''}
                                onChange={(e) => {
                                    setFilters({ ...filters, [field.field]: e.target.value })
                                }}
                                label={field.label}
                                type={field.type}
                                clearFunction={() => {
                                    setFilters({ ...filters, [field.field]: '' });
                                }}
                            />
                        </div>
                    );
                case 'date':
                    return (
                        <div key={field.field} className="p-field">
                            <Calendar
                                className="w-full md:w-14rem shadow-xl p-5 rounded-lg border-2"
                                id={field.field}
                                name={field.field}
                                value={filters[field.field] || null}
                                onChange={(e) => {
                                    const selectedDates = e.value;
                                    if (Array.isArray(selectedDates) && selectedDates.length === 2) {
                                        setFilters({
                                            ...filters,
                                            [field.field]: selectedDates,
                                        });
                                    } else {
                                        setFilters({
                                            ...filters,
                                            [field.field]: null,
                                        });
                                    }
                                }}
                                selectionMode="range"
                                placeholder={field.label || 'Tarih seçiniz'}
                                dateFormat="yy-mm-dd"
                                readOnlyInput
                                showIcon

                            />
                            {filters[field.field] && (
                                <i
                                    className="pi pi-times cursor-pointer absolute right-5 top-1/2 -translate-y-1/2"
                                    onClick={() => {
                                        setFilters({ ...filters, [field.field]: null });
                                    }}
                                />
                            )}
                        </div>
                    );
                case 'dropdown':
                    return (
                        <div key={field.field} className="p-field">
                            <Dropdown
                                id={field.field}
                                name={field.field}
                                value={filters[field.field] || null}
                                options={field.options}
                                onChange={(e) => {
                                    setFilters({ ...filters, [field.field]: e.value })
                                }}
                                placeholder={field.placeholder || 'Seçiniz'}
                                optionLabel={field.optionLabel || 'label'}
                                optionValue={field.optionValue || 'value'}
                                label={field.label}
                                showClear={true}
                                clearFunction={() => {
                                    setFilters({ ...filters, [field.field]: null });
                                }}
                            />
                        </div>
                    );
                default:
                    return null;
            }
        });
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-between align-center">
                <h5 className="m-0">{header}</h5>
                <span className="p-input-icon-left">
                    <Button
                        className='px-4 py-2 rounded-lg shadow-sm border hover:shadow-lg shadow-gray dark:shadow-white'
                        type="button"
                        label="Filtreler"
                        icon="pi pi-filter"
                        onClick={() => setVisibleSidebar(true)}
                    />
                </span>
            </div>
        );
    };

    return (
        <div>
            <Toast ref={toast} />

            <Sidebar
                header={<h1>Filtreler</h1>}
                visible={visibleSidebar}
                position="left"
                onHide={() => setVisibleSidebar(false)}
                className="p-sidebar-sm"
            >
                <form className="p-fluid space-y-8 mt-10">
                    {renderFilterInputs()}
                    <div className="flex justify-end gap-4 mt-4">
                        <Button
                            type="button"
                            label="Temizle"
                            icon="pi pi-times"
                            onClick={onFilterClear}
                            className="rounded-lg shadow-lg cursor-pointer text-red-700 hover:text-white border border-red-700 hover:bg-red-800 p-3"
                        />
                        <Button
                            type="button"
                            label="Filtrele"
                            icon="pi pi-filter"
                            onClick={onFilterApply}
                            className="rounded-lg shadow-lg cursor-pointer text-yellow-700 hover:text-white border border-yellow-700 hover:bg-yellow-800 p-3"
                        />
                    </div>
                </form>
            </Sidebar>
            <DataTable
                header={() => renderHeader()}
                value={data}
                lazy
                paginator
                totalRecords={totalRecords}
                loading={loading}
                first={lazyParams.first}
                rows={lazyParams.rows}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                onSortChange={onSort}
                responsiveLayout="scroll"
                emptyMessage="Kayıt bulunamadı."
                rowClassName={rowClassName}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                rowsPerPageOptions={[5, 10, 25, 50]}
                removableSort
                paginatorLeft={
                    <MultiSelect
                        value={visibleColumns}
                        options={columns}
                        optionLabel="header"
                        onChange={onColumnToggle}
                        className="w-full text-sm max-w-60"
                        display="comma"
                    />
                }
                paginatorRight={<><strong>Toplam Kayıt Sayısı:</strong> {totalRecords}</>}
                stripedRows
                className="custom-datatable"
            // pt={{
            //     headerCell: { className: 'bg-blue-500 text-white text-center py-0' },
            //     bodyCell: { className: 'py-0 text-sm' },
            //     headerRow: { className: 'bg-blue-500' }
            // }}
            >
                {visibleColumns.map((col) => (
                    <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        sortable={col.sortable}
                        body={col.body}
                        sortField={col.sortField || col.field}
                        className="py-0"
                        pt={{
                            headerCell: { className: 'bg-blue-500 text-white text-center py-0' },
                            bodyCell: { className: 'py-0 text-sm' }
                        }}
                    />
                ))}
            </DataTable>
        </div>
    );
};

DynamicDataTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            sortable: PropTypes.bool,
            body: PropTypes.func,
            sortField: PropTypes.string,
        })
    ).isRequired,
    filterFields: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(['text', 'number', 'date', 'dropdown']).isRequired,
            options: PropTypes.array,
            placeholder: PropTypes.string,
            optionLabel: PropTypes.string,
            optionValue: PropTypes.string,
        })
    ),
    data: PropTypes.array.isRequired,
    totalRecords: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    onLoadData: PropTypes.func.isRequired,
};

export default DynamicDataTable;